import React, { useState, useEffect } from 'react';
import { 
  FileText, Upload, Download, Search, Eye, Edit, Trash2, 
  Lock, Unlock, Clock, CheckCircle, AlertCircle, FolderOpen, 
  BookOpen, Brain, GitBranch, Save, X
} from 'lucide-react';
import axios from '../api/client';

const IntegratedDocumentSOP = () => {
  const [documents, setDocuments] = useState([]);
  const [sops, setSops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('documents'); // 'documents' or 'sops'
  
  // Modals
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDocTypeModal, setShowDocTypeModal] = useState(false);
  const [showAIAnalysisModal, setShowAIAnalysisModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  
  // Upload states
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadType, setUploadType] = useState(null); // 'new' or 'revision'
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [analyzingAI, setAnalyzingAI] = useState(false);
  
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  
  const [uploadData, setUploadData] = useState({
    document_name: '',
    document_type: 'SOP',
    category: 'Quality',
    department: '',
    description: '',
    version: '1.0',
    controlled: true,
    retention_period_years: 7,
    existing_sop_id: null
  });

  const documentTypes = ['SOP', 'Policy', 'Procedure', 'Form', 'Record', 'Manual', 'Specification', 'Report'];
  const categories = ['Quality', 'Safety', 'Operations', 'Training', 'Environmental', 'Maintenance'];
  const statuses = ['Draft', 'Under Review', 'Approved', 'Obsolete', 'Archived'];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [docsResponse, sopsResponse] = await Promise.all([
        axios.get('/documents'),
        axios.get('/sops')
      ]);
      setDocuments(docsResponse.data);
      setSops(sopsResponse.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Step 1: File selection opens doc type modal
  const handleFileSelect = async (file) => {
    setUploadedFile(file);
    setUploadData({ ...uploadData, document_name: file.name });
    setShowUploadModal(false);
    setShowDocTypeModal(true);
  };

  // Step 2: After selecting new/revision, analyze with AI
  const handleDocTypeSelection = async (type) => {
    setUploadType(type);
    setShowDocTypeModal(false);
    setShowAIAnalysisModal(true);
    
    // Analyze document with AI
    await analyzeDocument(uploadedFile);
  };

  const analyzeDocument = async (file) => {
    setAnalyzingAI(true);
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('analysis_type', uploadType);
    
    try {
      const response = await axios.post('/documents/analyze', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setAiAnalysis(response.data);
      
      // Pre-fill form with AI analysis
      setUploadData({
        ...uploadData,
        document_name: response.data.suggested_name || file.name,
        document_type: response.data.document_type || 'SOP',
        category: response.data.category || 'Quality',
        department: response.data.department || '',
        description: response.data.summary || '',
        version: response.data.suggested_version || '1.0',
        controlled: response.data.requires_control !== false,
        retention_period_years: response.data.retention_years || 7
      });
      
    } catch (error) {
      console.error('Error analyzing document:', error);
      alert('AI analysis failed. You can still upload manually.');
    } finally {
      setAnalyzingAI(false);
    }
  };

  // Step 3: Final upload with all data
  const handleFinalUpload = async (e) => {
    e.preventDefault();
    
    if (!uploadedFile) return;

    const formData = new FormData();
    formData.append('file', uploadedFile);
    formData.append('document_name', uploadData.document_name);
    formData.append('document_type', uploadData.document_type);
    formData.append('category', uploadData.category);
    formData.append('department', uploadData.department);
    formData.append('description', uploadData.description);
    formData.append('version', uploadData.version);
    formData.append('controlled', uploadData.controlled);
    formData.append('retention_period_years', uploadData.retention_period_years);
    formData.append('is_revision', uploadType === 'revision');
    formData.append('existing_sop_id', uploadData.existing_sop_id || '');
    formData.append('ai_analysis', JSON.stringify(aiAnalysis));

    try {
      const response = await axios.post('/documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setShowAIAnalysisModal(false);
      resetUploadState();
      fetchData();
      alert(`Document uploaded successfully! ${uploadType === 'revision' ? 'New version created.' : 'New document created.'}`);
    } catch (error) {
      console.error('Error uploading document:', error);
      alert('Failed to upload document');
    }
  };

  const resetUploadState = () => {
    setUploadedFile(null);
    setUploadType(null);
    setAiAnalysis(null);
    setUploadData({
      document_name: '',
      document_type: 'SOP',
      category: 'Quality',
      department: '',
      description: '',
      version: '1.0',
      controlled: true,
      retention_period_years: 7,
      existing_sop_id: null
    });
  };

  const handleDownload = async (docId, docName) => {
    try {
      const response = await axios.get(`/documents/${docId}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', docName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading document:', error);
      alert('Failed to download document');
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      await axios.delete(`/documents/${docId}`);
      fetchData();
      alert('Document deleted successfully');
    } catch (error) {
      console.error('Error deleting document:', error);
      alert('Failed to delete document');
    }
  };

  const getStatusColor = (status) => {
    switch(status?.toLowerCase()) {
      case 'approved': return 'bg-green-100 text-green-800';
      case 'under review': return 'bg-yellow-100 text-yellow-800';
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'obsolete': return 'bg-red-100 text-red-800';
      case 'archived': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch(status?.toLowerCase()) {
      case 'approved': return <CheckCircle size={16} className="text-green-600" />;
      case 'under review': return <Clock size={16} className="text-yellow-600" />;
      case 'draft': return <Edit size={16} className="text-gray-600" />;
      case 'obsolete': return <AlertCircle size={16} className="text-red-600" />;
      case 'archived': return <Lock size={16} className="text-blue-600" />;
      default: return <FileText size={16} />;
    }
  };

  const filteredItems = (activeTab === 'documents' ? documents : sops).filter(item => {
    const matchesSearch = item.document_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || item.document_type === filterType || item.type === filterType;
    const matchesStatus = filterStatus === 'all' || item.status === filterStatus;
    return matchesSearch && matchesType && matchesStatus;
  });

  const stats = {
    total: documents.length,
    sops: sops.length,
    approved: [...documents, ...sops].filter(d => d.status === 'Approved').length,
    underReview: [...documents, ...sops].filter(d => d.status === 'Under Review').length,
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center">
          <FolderOpen className="mr-3" />
          Integrated Document & SOP Management
        </h1>
        <p className="text-gray-600 mt-2">AI-powered document control with version tracking and compliance management</p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Total Documents</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <FileText className="text-blue-500" size={32} />
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">SOPs</p>
              <p className="text-2xl font-bold text-purple-600">{stats.sops}</p>
            </div>
            <BookOpen className="text-purple-500" size={32} />
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Approved</p>
              <p className="text-2xl font-bold text-green-600">{stats.approved}</p>
            </div>
            <CheckCircle className="text-green-500" size={32} />
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Under Review</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.underReview}</p>
            </div>
            <Clock className="text-yellow-500" size={32} />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="border-b border-gray-200">
          <div className="flex">
            <button
              onClick={() => setActiveTab('documents')}
              className={`px-6 py-3 font-medium ${
                activeTab === 'documents'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <FileText className="inline mr-2" size={20} />
              Document Control
            </button>
            <button
              onClick={() => setActiveTab('sops')}
              className={`px-6 py-3 font-medium ${
                activeTab === 'sops'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <BookOpen className="inline mr-2" size={20} />
              SOP Management
            </button>
          </div>
        </div>

        {/* Filters and Actions */}
        <div className="p-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4 flex-1">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Search documents..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Types</option>
                {documentTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Status</option>
                {statuses.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>
            <button
              onClick={() => setShowUploadModal(true)}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Upload size={20} className="mr-2" />
              Upload Document
            </button>
          </div>
        </div>
      </div>

      {/* Documents/SOPs Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Version</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredItems.map((item) => (
              <tr key={item.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <div className="flex items-center">
                    {activeTab === 'documents' ? (
                      <FileText className="text-blue-500 mr-3" size={20} />
                    ) : (
                      <BookOpen className="text-purple-500 mr-3" size={20} />
                    )}
                    <div>
                      <div className="font-medium text-gray-900">{item.document_name || item.title}</div>
                      {item.description && (
                        <div className="text-sm text-gray-500 truncate max-w-xs">{item.description}</div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">{item.document_type || item.type}</td>
                <td className="px-6 py-4 text-sm text-gray-900">{item.category}</td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  <div className="flex items-center">
                    <span>v{item.version || item.current_version}</span>
                    {item.revision_number > 0 && (
                      <GitBranch className="ml-2 text-gray-400" size={14} />
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center">
                    {getStatusIcon(item.status)}
                    <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                      {item.status}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  {item.upload_date || item.created_at ? new Date(item.upload_date || item.created_at).toLocaleDateString() : 'N/A'}
                </td>
                <td className="px-6 py-4 text-sm space-x-2">
                  <button
                    onClick={() => {
                      setSelectedDocument(item);
                      setShowDetailsModal(true);
                    }}
                    className="text-blue-600 hover:text-blue-800"
                    title="View Details"
                  >
                    <Eye size={16} className="inline" />
                  </button>
                  <button
                    onClick={() => handleDownload(item.id, item.document_name || item.title)}
                    className="text-green-600 hover:text-green-800"
                    title="Download"
                  >
                    <Download size={16} className="inline" />
                  </button>
                  <button
                    onClick={() => handleDelete(item.id)}
                    className="text-red-600 hover:text-red-800"
                    title="Delete"
                  >
                    <Trash2 size={16} className="inline" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredItems.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <FileText className="mx-auto mb-4 text-gray-400" size={48} />
            <p>No {activeTab === 'documents' ? 'documents' : 'SOPs'} found</p>
          </div>
        )}
      </div>

      {/* Step 1: Initial Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-2xl font-bold mb-4">Upload Document</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select File
                </label>
                <input
                  type="file"
                  onChange={(e) => {
                    if (e.target.files[0]) {
                      handleFileSelect(e.target.files[0]);
                    }
                  }}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  accept=".pdf,.doc,.docx,.txt"
                />
              </div>
              <p className="text-sm text-gray-500">
                Supported formats: PDF, DOC, DOCX, TXT
              </p>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowUploadModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Document Type Selection Modal (New or Revision) */}
      {showDocTypeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-2xl font-bold mb-4 flex items-center">
              <GitBranch className="mr-2" />
              Document Type
            </h2>
            <p className="text-gray-600 mb-6">
              Is this a new document or a revision of an existing one?
            </p>
            <div className="space-y-4">
              <button
                onClick={() => handleDocTypeSelection('new')}
                className="w-full p-4 border-2 border-blue-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 text-left transition-colors"
              >
                <div className="flex items-center">
                  <FileText className="text-blue-600 mr-3" size={24} />
                  <div>
                    <div className="font-semibold text-gray-900">New Document</div>
                    <div className="text-sm text-gray-600">Create a new document entry</div>
                  </div>
                </div>
              </button>
              <button
                onClick={() => handleDocTypeSelection('revision')}
                className="w-full p-4 border-2 border-purple-200 rounded-lg hover:border-purple-500 hover:bg-purple-50 text-left transition-colors"
              >
                <div className="flex items-center">
                  <GitBranch className="text-purple-600 mr-3" size={24} />
                  <div>
                    <div className="font-semibold text-gray-900">Revision/Update</div>
                    <div className="text-sm text-gray-600">Update an existing document with a new version</div>
                  </div>
                </div>
              </button>
            </div>
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => {
                  setShowDocTypeModal(false);
                  setShowUploadModal(true);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Back
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Step 3: AI Analysis & Final Upload Modal */}
      {showAIAnalysisModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold flex items-center">
                <Brain className="mr-2 text-purple-600" />
                AI Analysis & Upload
              </h2>
              <button
                onClick={() => {
                  setShowAIAnalysisModal(false);
                  resetUploadState();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>

            {analyzingAI ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Analyzing document with AI...</p>
                <p className="text-sm text-gray-500 mt-2">This may take a few moments</p>
              </div>
            ) : (
              <form onSubmit={handleFinalUpload}>
                {/* AI Analysis Results */}
                {aiAnalysis && (
                  <div className="mb-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                    <h3 className="font-semibold text-purple-900 mb-2 flex items-center">
                      <Brain size={18} className="mr-2" />
                      AI Analysis Results
                    </h3>
                    <div className="space-y-2 text-sm">
                      <p><strong>Document Type:</strong> {aiAnalysis.document_type}</p>
                      <p><strong>Category:</strong> {aiAnalysis.category}</p>
                      <p><strong>Confidence:</strong> {aiAnalysis.confidence}%</p>
                      {aiAnalysis.summary && (
                        <p><strong>Summary:</strong> {aiAnalysis.summary}</p>
                      )}
                      {aiAnalysis.key_topics && (
                        <p><strong>Key Topics:</strong> {aiAnalysis.key_topics.join(', ')}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Form Fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {uploadType === 'revision' && (
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Select Existing Document*
                      </label>
                      <select
                        required
                        value={uploadData.existing_sop_id || ''}
                        onChange={(e) => setUploadData({...uploadData, existing_sop_id: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select document to revise...</option>
                        {documents.map(doc => (
                          <option key={doc.id} value={doc.id}>
                            {doc.document_name} (v{doc.version})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Document Name*
                    </label>
                    <input
                      type="text"
                      required
                      value={uploadData.document_name}
                      onChange={(e) => setUploadData({...uploadData, document_name: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Document Type*
                    </label>
                    <select
                      required
                      value={uploadData.document_type}
                      onChange={(e) => setUploadData({...uploadData, document_type: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      {documentTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Category*
                    </label>
                    <select
                      required
                      value={uploadData.category}
                      onChange={(e) => setUploadData({...uploadData, category: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Department*
                    </label>
                    <input
                      type="text"
                      required
                      value={uploadData.department}
                      onChange={(e) => setUploadData({...uploadData, department: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Version
                    </label>
                    <input
                      type="text"
                      value={uploadData.version}
                      onChange={(e) => setUploadData({...uploadData, version: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Retention Period (Years)
                    </label>
                    <input
                      type="number"
                      value={uploadData.retention_period_years}
                      onChange={(e) => setUploadData({...uploadData, retention_period_years: parseInt(e.target.value)})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={uploadData.controlled}
                        onChange={(e) => setUploadData({...uploadData, controlled: e.target.checked})}
                        className="mr-2"
                      />
                      <span className="text-sm font-medium text-gray-700">Controlled Document</span>
                    </label>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      value={uploadData.description}
                      onChange={(e) => setUploadData({...uploadData, description: e.target.value})}
                      rows="3"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAIAnalysisModal(false);
                      setShowDocTypeModal(true);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    <Save size={20} className="mr-2" />
                    Upload Document
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedDocument && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-4">Document Details</h2>
            <div className="space-y-3">
              <div><strong>Name:</strong> {selectedDocument.document_name || selectedDocument.title}</div>
              <div><strong>Type:</strong> {selectedDocument.document_type || selectedDocument.type}</div>
              <div><strong>Category:</strong> {selectedDocument.category}</div>
              <div><strong>Department:</strong> {selectedDocument.department}</div>
              <div><strong>Version:</strong> v{selectedDocument.version || selectedDocument.current_version}</div>
              <div><strong>Status:</strong> {selectedDocument.status}</div>
              {selectedDocument.controlled !== undefined && (
                <div><strong>Controlled:</strong> {selectedDocument.controlled ? 'Yes' : 'No'}</div>
              )}
              {selectedDocument.retention_period_years && (
                <div><strong>Retention Period:</strong> {selectedDocument.retention_period_years} years</div>
              )}
              <div><strong>Date:</strong> {selectedDocument.upload_date || selectedDocument.created_at ? new Date(selectedDocument.upload_date || selectedDocument.created_at).toLocaleString() : 'N/A'}</div>
              {selectedDocument.description && (
                <div><strong>Description:</strong> {selectedDocument.description}</div>
              )}
              {selectedDocument.ai_analysis && (
                <div className="mt-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                  <strong className="flex items-center mb-2">
                    <Brain size={18} className="mr-2 text-purple-600" />
                    AI Analysis:
                  </strong>
                  <pre className="text-sm whitespace-pre-wrap">{JSON.stringify(selectedDocument.ai_analysis, null, 2)}</pre>
                </div>
              )}
            </div>
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowDetailsModal(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IntegratedDocumentSOP;
